import { useAlert } from "clients/admin/src/desktop/contexts/AlertProvider";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
  type ChangeEvent,
} from "react";

import { Avatar, Box, Button, CircularProgress, Stack } from "@mui/material";
import Cropper, { type Area, type MediaSize } from "react-easy-crop";

import { sameOriginImageURL } from "@shared/helpers/constants";
import { linko } from "@shared/linko";
import clsx from "clsx";
import { useOrganization } from "../../../contexts/OrganizationProvider";
import "./ImageUploader.scss";

interface UploadImageProps {
  name: string;
  handleImageChange?: (imageId: string) => void;
  imageId?: string;
  outOfForm?: boolean;
  isBackground?: boolean;
  /** Preview with actions under the image - for tight admin forms */
  compact?: boolean;
  compactSize?: number;
}

export type UploadImageHandle = {
  /** מעלה crop ממתין (אם יש) ומחזיר את מזהה התמונה */
  uploadPending: () => Promise<string | undefined>;
};

interface PostImageType {
  myFile: string;
  imageId: string;
}

function getFileInputId(name: string) {
  return `upload-image-file-${name}`;
}

function getDefaultSquareCropArea(
  naturalWidth: number,
  naturalHeight: number,
): Area {
  const size = Math.min(naturalWidth, naturalHeight);
  return {
    x: (naturalWidth - size) / 2,
    y: (naturalHeight - size) / 2,
    width: size,
    height: size,
  };
}

export const UploadImage = forwardRef<UploadImageHandle, UploadImageProps>(
  function UploadImage(
    {
      handleImageChange,
      imageId,
      name,
      outOfForm = true,
      isBackground,
      compact = false,
      compactSize = 84,
    },
    ref,
  ) {
    const fileInputId = getFileInputId(name);
    const [postImage, setPostImage] = useState<PostImageType>({
      myFile: "",
      imageId: "",
    });
    const { currOrganization } = useOrganization();
    const [crop, setCrop] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
    const [zoom, setZoom] = useState<number>(1);
    const { setAlertText, setSeverity } = useAlert();
    const [croppedImage, setCroppedImage] = useState<ArrayBuffer>();
    const [imageSrc, setImageSrc] = useState<string>();
    const [mediaSize, setMediaSize] = useState<MediaSize | null>(null);
    const [loading, setLoading] = useState(false);

    const resolvedImageId = (imageId || postImage.imageId || "").trim();

    useEffect(() => {
      setPostImage((prev) => ({ ...prev, imageId: imageId || "" }));
    }, [imageId]);

    const resetPickerState = useCallback(() => {
      setImageSrc(undefined);
      setCroppedImage(undefined);
      setMediaSize(null);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    }, []);

    function deletePhoto() {
      setPostImage({ myFile: "", imageId: "" });
      resetPickerState();
      if (outOfForm && handleImageChange) {
        handleImageChange("");
      }
    }

    const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files && e.target.files[0];
      e.target.value = "";
      if (file) {
        const reader = new FileReader();
        reader.onload = () => {
          setImageSrc(reader.result as string);
          setCroppedImage(undefined);
          setMediaSize(null);
        };
        reader.readAsDataURL(file);
      }
    };

    const getCroppedImage = useCallback(
      (
        source: string,
        croppedAreaPixels: Area,
      ): Promise<{ image: ArrayBuffer }> => {
        return new Promise((resolve, reject) => {
          const img = new Image();
          img.src = source;

          img.onload = () => {
            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");

            if (!ctx) {
              reject(new Error("Canvas context is null"));
              return;
            }

            const diameter = Math.min(
              croppedAreaPixels.width,
              croppedAreaPixels.height,
            );
            const finalWidth = isBackground ? 350 : diameter;
            const finalHeight = isBackground ? 120 : diameter;

            canvas.width = finalWidth;
            canvas.height = finalHeight;

            ctx.fillStyle = "white";
            ctx.fillRect(0, 0, finalWidth, finalHeight);

            ctx.save();

            if (!isBackground) {
              ctx.beginPath();
              ctx.arc(diameter / 2, diameter / 2, diameter / 2, 0, Math.PI * 2);
              ctx.clip();
            }

            ctx.drawImage(
              img,
              croppedAreaPixels.x,
              croppedAreaPixels.y,
              croppedAreaPixels.width,
              croppedAreaPixels.height,
              0,
              0,
              isBackground ? 350 : diameter,
              isBackground ? 120 : diameter,
            );

            ctx.restore();

            canvas.toBlob(
              async (blob) => {
                if (blob) {
                  try {
                    const arrayBuffer = await blob.arrayBuffer();
                    resolve({ image: arrayBuffer });
                  } catch (error) {
                    reject(error);
                  }
                } else {
                  reject(new Error("Failed to create blob from canvas"));
                }
              },
              "image/jpeg",
              0.95,
            );
          };

          img.onerror = (error) => {
            reject(error);
          };
        });
      },
      [isBackground],
    );

    const buildCropBuffer = useCallback(async (): Promise<ArrayBuffer | undefined> => {
      if (croppedImage) return croppedImage;
      if (!imageSrc || !mediaSize) return undefined;

      const croppedAreaPixels = getDefaultSquareCropArea(
        mediaSize.naturalWidth,
        mediaSize.naturalHeight,
      );
      const cropped = await getCroppedImage(imageSrc, croppedAreaPixels);
      setCroppedImage(cropped.image);
      return cropped.image;
    }, [croppedImage, getCroppedImage, imageSrc, mediaSize]);

    const onCropComplete = useCallback(
      async (_: Area, croppedAreaPixels: Area) => {
        if (!imageSrc) return;
        const cropped = await getCroppedImage(imageSrc, croppedAreaPixels);
        setCroppedImage(cropped.image);
      },
      [getCroppedImage, imageSrc],
    );

    const onMediaLoaded = useCallback(
      (size: MediaSize) => {
        setMediaSize(size);
        if (!imageSrc) return;
        void (async () => {
          const croppedAreaPixels = getDefaultSquareCropArea(
            size.naturalWidth,
            size.naturalHeight,
          );
          const cropped = await getCroppedImage(imageSrc, croppedAreaPixels);
          setCroppedImage(cropped.image);
        })();
      },
      [getCroppedImage, imageSrc],
    );

    const uploadCroppedImage = useCallback(async (): Promise<string | undefined> => {
      const buffer = await buildCropBuffer();
      if (!buffer) {
        return resolvedImageId || undefined;
      }

      setLoading(true);
      try {
        const { data: image } = await linko.image.mutations.createImage(
          {
            organizationId: currOrganization?.id,
            isBackground: !!isBackground,
          },
          {
            file: new File([buffer], "image.jpeg", { type: "image/jpeg" }),
          },
        );

        if (!image?.id) {
          throw new Error("Image upload returned no id");
        }

        setPostImage((prev) => ({ ...prev, imageId: image.id }));
        resetPickerState();
        handleImageChange?.(image.id);
        return image.id;
      } catch (e) {
        console.error("Upload error:", e);
        setSeverity("error");
        setAlertText("לא היה ניתן להעלות את התמונה");
        throw e;
      } finally {
        setLoading(false);
      }
    }, [
      buildCropBuffer,
      currOrganization?.id,
      handleImageChange,
      isBackground,
      resetPickerState,
      resolvedImageId,
      setAlertText,
      setSeverity,
    ]);

    useImperativeHandle(
      ref,
      () => ({
        uploadPending: uploadCroppedImage,
      }),
      [uploadCroppedImage],
    );

    async function upload() {
      try {
        if (!imageSrc && !croppedImage) {
          setSeverity("warning");
          setAlertText("לא נבחרה תמונה");
          return;
        }
        await uploadCroppedImage();
      } catch {
        /* handled in uploadCroppedImage */
      }
    }

    const hasPendingCrop = !!imageSrc;
    const showUploadButton = hasPendingCrop && !!croppedImage;
    const showDeleteButton = !!resolvedImageId && !imageSrc;

    if (compact) {
      const fileInput = (
        <input
          id={fileInputId}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          style={{ display: "none" }}
        />
      );
      return (
        <Stack alignItems="center" gap={0.75} sx={{ width: compactSize }}>
          <Box
            component="label"
            htmlFor={loading || imageSrc ? undefined : fileInputId}
            sx={{
              width: compactSize,
              height: compactSize,
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: loading || imageSrc ? "default" : "pointer",
              bgcolor: "grey.50",
              position: "relative",
              boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.02)",
            }}
          >
            {loading ? (
              <CircularProgress size={26} />
            ) : imageSrc ? (
              <Box
                sx={{
                  width: compactSize,
                  height: compactSize,
                  position: "relative",
                }}
              >
                <Cropper
                  image={imageSrc}
                  crop={crop}
                  zoom={zoom}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                  onMediaLoaded={onMediaLoaded}
                  zoomSpeed={0.25}
                  aspect={1}
                  cropShape="rect"
                />
              </Box>
            ) : resolvedImageId ? (
              <Avatar
                src={sameOriginImageURL(resolvedImageId)}
                variant="rounded"
                imgProps={{ referrerPolicy: "no-referrer" }}
                sx={{
                  width: compactSize,
                  height: compactSize,
                  borderRadius: 3,
                }}
              />
            ) : (
              <Box
                component="img"
                className="empty-icon"
                src="/upload-image.png"
                alt=""
                sx={{ width: 36, height: 36, opacity: 0.5 }}
              />
            )}
            {fileInput}
          </Box>
          {showUploadButton ? (
            <Button
              size="small"
              onClick={() => void upload()}
              sx={{ textTransform: "none" }}
            >
              העלאה
            </Button>
          ) : showDeleteButton ? (
            <Stack direction="row" gap={0.25}>
              <Button
                component="label"
                htmlFor={fileInputId}
                size="small"
                sx={{ textTransform: "none", minWidth: 0, px: 0.75 }}
              >
                החלפה
              </Button>
              <Button
                size="small"
                onClick={deletePhoto}
                sx={{
                  textTransform: "none",
                  minWidth: 0,
                  px: 0.75,
                  color: "text.secondary",
                }}
              >
                מחיקה
              </Button>
            </Stack>
          ) : null}
        </Stack>
      );
    }

    return (
      <div className="upload-image-container-desktop">
        <div className={clsx("image-container", isBackground && " background")}>
          {loading ? (
            <CircularProgress />
          ) : imageSrc ? (
            <Cropper
              classes={{
                containerClassName: clsx(
                  "image-uploader cropper",
                  isBackground && " background",
                ),
              }}
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
              onMediaLoaded={onMediaLoaded}
              zoomSpeed={0.25}
              aspect={1}
              cropShape={isBackground ? "rect" : "round"}
            />
          ) : resolvedImageId ? (
            <Stack alignItems="center" gap={1}>
              <Avatar
                src={sameOriginImageURL(resolvedImageId)}
                className="image-uploader"
                imgProps={{ referrerPolicy: "no-referrer" }}
                style={{ width: "7rem", height: "7rem" }}
              />
              <label htmlFor={fileInputId}>
                <Button component="span" size="small" variant="outlined">
                  {isBackground ? "החלפת תמונה" : "החלפת לוגו"}
                </Button>
              </label>
              <input
                id={fileInputId}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                style={{ display: "none" }}
              />
            </Stack>
          ) : (
            <>
              <label className="image-uploader" htmlFor={fileInputId}>
                <img className="empty-icon" src="/upload-image.png" alt="" />
              </label>
              <input
                id={fileInputId}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                style={{ display: "none" }}
              />
            </>
          )}
        </div>
        {showUploadButton ? (
          <Button className="upload-image-button" onClick={() => void upload()}>
            העלאת תמונה
          </Button>
        ) : showDeleteButton ? (
          <Button className="upload-image-button" onClick={deletePhoto}>
            מחיקה
          </Button>
        ) : null}
      </div>
    );
  },
);
